import { forwardRef, useRef } from 'react'
import type { ButtonHTMLAttributes, MouseEvent, PointerEvent, ReactNode } from 'react'
import { Tip } from './Hover/Tip'
import type { GlobalActionLoadingOptions } from '../../context/globalActionLoadingContextCore'
import { useOptionalGlobalActionLoading } from '../../context/useGlobalActionLoading'
import type { ZapButtonSize, ZapButtonTone, ZapButtonVariant } from './Button'
import { emitZap } from './zapRipple'

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: ReactNode
  label: string
  variant?: ZapButtonVariant
  size?: ZapButtonSize
  tone?: ZapButtonTone
  loading?: boolean
  globalLoading?: boolean | GlobalActionLoadingOptions
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right'
  tipGroup?: string
  shortcut?: string
  pressed?: boolean
}

export const IconButton = forwardRef<HTMLButtonElement, Props>(function IconButton(
  {
    icon,
    label,
    variant = 'tertiary',
    size = 'md',
    tone,
    loading = false,
    globalLoading = false,
    tooltipPlacement = 'top',
    tipGroup,
    shortcut,
    disabled,
    pressed,
    className,
    onPointerDown,
    onClick,
    'aria-label': ariaLabelProp,
    ...rest
  },
  ref,
) {
  const localRef = useRef<HTMLButtonElement | null>(null)
  const globalActionLoading = useOptionalGlobalActionLoading()
  const globallyDisabled = Boolean(globalLoading && globalActionLoading?.isActionLoading)

  const handlePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    onPointerDown?.(event)
    if (event.defaultPrevented || disabled || loading || globallyDisabled) return
    if (variant === 'primary') {
      const el = event.currentTarget
      const rect = el.getBoundingClientRect()
      emitZap(el, event.clientX - rect.left, event.clientY - rect.top)
    }
  }

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (loading || globallyDisabled) {
      event.preventDefault()
      return
    }
    const result = onClick?.(event) as unknown
    if (
      globalLoading &&
      result &&
      typeof (result as Promise<unknown>).then === 'function' &&
      globalActionLoading
    ) {
      const options = typeof globalLoading === 'object' ? globalLoading : undefined
      void globalActionLoading.runWithGlobalLoading(() => result as Promise<unknown>, options)
    }
  }

  const isDanger = tone === 'danger'
  const variantClass = variant === 'danger' ? 'zapBtn--dangerSolid' : `zapBtn--${variant}`

  const classes = [
    'zapBtn',
    'zapBtn--icon',
    variantClass,
    `zapBtn--${size}`,
    isDanger ? 'zapBtn--danger' : '',
    pressed ? 'is-pressed' : '',
    loading ? 'is-loading' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  const button = (
    <button
      ref={(node) => {
        localRef.current = node
        if (typeof ref === 'function') ref(node)
        else if (ref) (ref as { current: HTMLButtonElement | null }).current = node
      }}
      type={rest.type ?? 'button'}
      className={classes}
      disabled={disabled || loading || globallyDisabled}
      aria-label={ariaLabelProp ?? label}
      aria-pressed={pressed !== undefined ? pressed : rest['aria-pressed']}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      {...rest}
    >
      <span className="zapBtn__glow" aria-hidden="true" />
      <span className="zapBtn__charge" aria-hidden="true" />
      {loading ? (
        <span className="zapBtn__spark" aria-hidden="true">
          <span className="zapBtn__sparkCore" />
          <span className="zapBtn__sparkArc zapBtn__sparkArc--a" />
          <span className="zapBtn__sparkArc zapBtn__sparkArc--b" />
        </span>
      ) : (
        <span className="zapBtn__icon zapBtn__icon--only" aria-hidden="true">{icon}</span>
      )}
    </button>
  )

  if (!label) return button
  return (
    <Tip label={label} placement={tooltipPlacement} group={tipGroup} shortcut={shortcut}>
      {disabled || loading || globallyDisabled ? <span className="zapBtnTooltipAnchor">{button}</span> : button}
    </Tip>
  )
})
